import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import './EcxSidebar.css';

const NAV_THEMES = {
    primary: {
        iconClass: 'ecx-nav-icon--primary',
        activeClass: 'ecx-nav-active ecx-nav-active--primary',
        idleClass: 'ecx-nav-idle ecx-nav-idle--primary',
    },
    purple: {
        iconClass: 'ecx-nav-icon--purple',
        activeClass: 'ecx-nav-active ecx-nav-active--purple',
        idleClass: 'ecx-nav-idle ecx-nav-idle--purple',
    },
    cyan: {
        iconClass: 'ecx-nav-icon--cyan',
        activeClass: 'ecx-nav-active ecx-nav-active--cyan',
        idleClass: 'ecx-nav-idle ecx-nav-idle--cyan',
    },
    indigo: {
        iconClass: 'ecx-nav-icon--indigo',
        activeClass: 'ecx-nav-active ecx-nav-active--indigo',
        idleClass: 'ecx-nav-idle ecx-nav-idle--indigo',
    },
    success: {
        iconClass: 'ecx-nav-icon--success',
        activeClass: 'ecx-nav-active ecx-nav-active--success',
        idleClass: 'ecx-nav-idle ecx-nav-idle--success',
    },
    teal: {
        iconClass: 'ecx-nav-icon--teal',
        activeClass: 'ecx-nav-active ecx-nav-active--teal',
        idleClass: 'ecx-nav-idle ecx-nav-idle--teal',
    },
    orange: {
        iconClass: 'ecx-nav-icon--orange',
        activeClass: 'ecx-nav-active ecx-nav-active--orange',
        idleClass: 'ecx-nav-idle ecx-nav-idle--orange',
    },
    pink: {
        iconClass: 'ecx-nav-icon--pink',
        activeClass: 'ecx-nav-active ecx-nav-active--pink',
        idleClass: 'ecx-nav-idle ecx-nav-idle--pink',
    },
    danger: {
        iconClass: 'ecx-nav-icon--danger',
        activeClass: 'ecx-nav-active ecx-nav-active--danger',
        idleClass: 'ecx-nav-idle ecx-nav-idle--danger',
    },
    warning: {
        iconClass: 'ecx-nav-icon--warning',
        activeClass: 'ecx-nav-active ecx-nav-active--warning',
        idleClass: 'ecx-nav-idle ecx-nav-idle--warning',
    },
    slate: {
        iconClass: 'ecx-nav-icon--slate',
        activeClass: 'ecx-nav-active ecx-nav-active--slate',
        idleClass: 'ecx-nav-idle ecx-nav-idle--slate',
    },
    violet: {
        iconClass: 'ecx-nav-icon--violet',
        activeClass: 'ecx-nav-active ecx-nav-active--violet',
        idleClass: 'ecx-nav-idle ecx-nav-idle--violet',
    },
    info: {
        iconClass: 'ecx-nav-icon--info',
        activeClass: 'ecx-nav-active ecx-nav-active--info',
        idleClass: 'ecx-nav-idle ecx-nav-idle--info',
    },
};

const sidebarItems = [
    {
        label: 'Dashboard',
        icon: 'bi-speedometer2',
        to: '/dashboard',
        theme: 'primary',
    },
    {
        label: 'Appointments',
        icon: 'bi-calendar-event',
        to: '/appointments',
        theme: 'purple',
    },
    {
        label: 'Web Bookings',
        icon: 'bi-calendar-check',
        to: '/web-bookings',
        theme: 'cyan',
    },
    {
        label: 'Patient Files',
        icon: 'bi-folder2-open',
        to: '/patient-files',
        theme: 'indigo',
    },
    { type: 'divider', label: 'Communication Tools', accent: 'communication' },
    {
        label: 'Messages',
        icon: 'bi-chat-left-text',
        to: '/messages',
        theme: 'success',
        badgeKey: 'smsInbox',
    },
    {
        label: 'Surveys',
        icon: 'bi-clipboard-check',
        to: '/surveys',
        theme: 'teal',
    },
    {
        label: 'Articles',
        icon: 'bi-file-text',
        to: '/articles',
        theme: 'orange',
    },
    {
        label: 'Newsletter',
        icon: 'bi-envelope',
        submenuKey: 'newsletter',
        theme: 'pink',
        submenu: [
            { label: 'Email Newsletter', icon: 'bi-envelope-paper', to: '/newsletter/email-newsletter' },
            { label: 'SMS Newsletter', icon: 'bi-chat-text', to: '/newsletter/sms-newsletter' },
        ],
    },
    {
        label: 'Blast',
        icon: 'bi-megaphone',
        submenuKey: 'blast',
        theme: 'danger',
        submenu: [
            { label: 'Email Blast', icon: 'bi-send', to: '/blast/email' },
            { label: 'SMS Blast', icon: 'bi-chat-dots', to: '/blast/sms' },
        ],
    },
    {
        label: 'Reputation',
        icon: 'bi-star',
        to: '/reputation',
        theme: 'warning',
    },
    { type: 'divider', label: 'Misc', accent: 'misc' },
    {
        label: 'My Files',
        icon: 'bi-folder',
        to: '/my-files',
        theme: 'slate',
    },
    {
        label: 'PMS Update History',
        icon: 'bi-arrow-repeat',
        to: '/pms-update-history',
        theme: 'cyan',
    },
    {
        label: 'Activity',
        icon: 'bi-clock-history',
        to: '/activity',
        theme: 'violet',
    },
    {
        label: 'Settings',
        icon: 'bi-gear',
        submenuKey: 'settings',
        theme: 'info',
        submenu: [
            { label: 'Clinic', icon: 'bi-building', to: '/settings/clinic' },
            { label: 'Reminders', icon: 'bi-bell', to: '/settings/reminders' },
            { label: 'Users', icon: 'bi-people', to: '/settings/users' },
            { label: 'Eyeconx Features', icon: 'bi-stars', to: '/settings/features' },
            { label: 'Templates', icon: 'bi-layout-text-window', to: '/settings/templates' },
        ],
    },
];

const getTheme = (themeKey) => NAV_THEMES[themeKey] || NAV_THEMES.primary;

function NavIcon({ icon, themeKey, collapsed }) {
    const theme = getTheme(themeKey);

    return (
        <span className={`ecx-nav-icon ${theme.iconClass} ${collapsed ? '' : 'me-2'}`}>
            <i className={`bi ${icon}`}></i>
        </span>
    );
}

function NavItem({ to, icon, label, themeKey, collapsed, badge, onNavigate }) {
    const theme = getTheme(themeKey);
    const tooltipId = `ecx-nav-${to.replace(/\//g, '-')}`;

    return (
        <li className="nav-item">
            <NavLink
                to={to}
                className={({ isActive }) =>
                    `nav-link rounded-3 d-flex align-items-center py-2 px-2 fw-medium ${
                        collapsed ? 'justify-content-center position-relative' : ''
                    } ${isActive ? theme.activeClass : theme.idleClass}`
                }
                onClick={onNavigate}
                data-tooltip-id={collapsed ? tooltipId : undefined}
                data-tooltip-content={collapsed ? label : undefined}
                data-tooltip-place="right"
            >
                <NavIcon icon={icon} themeKey={themeKey} collapsed={collapsed} />
                {!collapsed && <span>{label}</span>}
                {badge > 0 && (
                    <span
                        className={`badge rounded-pill bg-success ${
                            collapsed ? 'position-absolute top-0 end-0' : 'ms-auto'
                        }`}
                        style={{ fontSize: '0.65rem' }}
                    >
                        {badge}
                    </span>
                )}
            </NavLink>
            {collapsed && <Tooltip id={tooltipId} />}
        </li>
    );
}

function SubNavIcon({ icon, themeKey }) {
    const theme = getTheme(themeKey);

    return (
        <span className={`ecx-nav-subicon ${theme.iconClass}`}>
            <i className={`bi ${icon}`}></i>
        </span>
    );
}

function SubmenuLink({ sub, themeKey, className, onClick }) {
    return (
        <NavLink
            to={sub.to}
            className={className}
            onClick={onClick}
        >
            <SubNavIcon icon={sub.icon} themeKey={themeKey} />
            <span className="ecx-submenu-label">{sub.label}</span>
        </NavLink>
    );
}

function SubmenuItem({
    item,
    collapsed,
    openSubmenus,
    toggleSubmenu,
    hoveredSubmenu,
    setHoveredSubmenu,
    submenuPosition,
    setSubmenuPosition,
    location,
    onNavigate,
}) {
    const theme = getTheme(item.theme);
    const isSubmenuActive = item.submenu.some((sub) => location.pathname.startsWith(sub.to));
    const tooltipId = `ecx-nav-submenu-${item.submenuKey}`;

    const renderPopover = () =>
        collapsed && hoveredSubmenu === item.submenuKey ? (
            <div
                className="position-fixed bg-white border rounded shadow-sm ecx-submenu-popover"
                style={{
                    top: submenuPosition.top,
                    left: submenuPosition.left + 4,
                }}
                onMouseEnter={() => setHoveredSubmenu(item.submenuKey)}
                onMouseLeave={() => setHoveredSubmenu(null)}
            >
                <div className="ecx-submenu-popover__title px-2 pt-2 pb-1">{item.label}</div>
                <ul className="nav nav-pills flex-column p-2 pt-1 gap-1 mb-0">
                    {item.submenu.map((sub) => (
                        <li className="nav-item" key={sub.to}>
                            <SubmenuLink
                                sub={sub}
                                themeKey={item.theme}
                                className={({ isActive }) =>
                                    `nav-link rounded-3 py-2 px-2 fw-medium d-flex align-items-center ${
                                        isActive ? theme.activeClass : theme.idleClass
                                    }`
                                }
                                onClick={() => {
                                    setHoveredSubmenu(null);
                                    onNavigate?.();
                                }}
                            />
                        </li>
                    ))}
                </ul>
            </div>
        ) : null;

    return (
        <li className="nav-item position-relative">
            <button
                type="button"
                className={`nav-link rounded-3 d-flex align-items-center py-2 px-2 w-100 border-0 submenu-toggle fw-medium ${
                    collapsed ? 'justify-content-center' : ''
                } ${isSubmenuActive ? theme.activeClass : theme.idleClass}`}
                onClick={(e) => toggleSubmenu(item.submenuKey, e)}
                onMouseEnter={(e) => {
                    if (collapsed) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setSubmenuPosition({ top: rect.top, left: rect.right });
                        setHoveredSubmenu(item.submenuKey);
                    }
                }}
                data-tooltip-id={collapsed && hoveredSubmenu !== item.submenuKey ? tooltipId : undefined}
                data-tooltip-content={collapsed ? item.label : undefined}
                data-tooltip-place="right"
            >
                <NavIcon icon={item.icon} themeKey={item.theme} collapsed={collapsed} />
                {!collapsed && <span className="flex-fill text-start">{item.label}</span>}
                {!collapsed && (
                    <i className={`bi ${openSubmenus[item.submenuKey] ? 'bi-chevron-up' : 'bi-chevron-down'} ms-auto`}></i>
                )}
            </button>
            {collapsed && hoveredSubmenu !== item.submenuKey && <Tooltip id={tooltipId} />}
            {renderPopover()}
            {!collapsed && openSubmenus[item.submenuKey] && (
                <ul className="nav nav-pills flex-column gap-1 mb-1">
                    {item.submenu.map((sub) => (
                        <li className="nav-item" key={sub.to}>
                            <SubmenuLink
                                sub={sub}
                                themeKey={item.theme}
                                className={({ isActive }) =>
                                    `nav-link rounded-3 ecx-sidebar-sublink py-2 px-2 fw-medium d-flex align-items-center ${
                                        isActive ? theme.activeClass : theme.idleClass
                                    }`
                                }
                                onClick={onNavigate}
                            />
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
}

function EcxSidebar({ collapsed = false, mobile = false }) {
    const location = useLocation();
    const navigate = useNavigate();
    const logout = useEyeconxStore((state) => state.logout);
    const smsInbox = useEyeconxStore((state) => state.smsInbox);
    const sidebarToggled = useEyeconxStore((state) => state.sidebarToggled);
    const sidebarBroken = useEyeconxStore((state) => state.sidebarBroken);
    const setSidebarCollapsed = useEyeconxStore((state) => state.setSidebarCollapsed);
    const toggleSidebar = useEyeconxStore((state) => state.toggleSidebar);
    const setSidebarBroken = useEyeconxStore((state) => state.setSidebarBroken);

    const [openSubmenus, setOpenSubmenus] = useState({});
    const [hoveredSubmenu, setHoveredSubmenu] = useState(null);
    const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        const handleResize = () => setSidebarBroken(window.innerWidth <= 767);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [setSidebarBroken]);

    useEffect(() => {
        setOpenSubmenus((prev) => {
            const next = { ...prev };
            sidebarItems.forEach((item) => {
                if (item.submenu?.some((sub) => location.pathname.startsWith(sub.to))) {
                    next[item.submenuKey] = true;
                }
            });
            return next;
        });
    }, [location.pathname]);

    const toggleSubmenu = (key, event) => {
        if (collapsed) {
            const rect = event.currentTarget.getBoundingClientRect();
            setSubmenuPosition({ top: rect.top, left: rect.right });
            setHoveredSubmenu((prev) => (prev === key ? null : key));
        } else {
            setOpenSubmenus((prev) => ({ ...prev, [key]: !prev[key] }));
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const closeMobileSidebar = () => {
        if (mobile && sidebarToggled) {
            toggleSidebar();
        }
    };

    const sidebarContent = (
        <div className={`ecx-sidebar-inner ${collapsed ? 'ecx-sidebar--collapsed' : ''}`}>
            <div className="ecx-sidebar-header">
                <div className="ecx-sidebar-logo">
                    {collapsed ? (
                        <span className="ecx-sidebar-logo-compact d-inline-flex align-items-center justify-content-center">
                            E
                        </span>
                    ) : (
                        <img
                            src="/media/logos2/neweyeconxlogo.png"
                            alt="EyeconX"
                            className="img-fluid"
                        />
                    )}
                </div>
                {!mobile && (
                    <button
                        type="button"
                        className="ecx-sidebar-toggle"
                        onClick={() => setSidebarCollapsed(!collapsed)}
                        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        title={collapsed ? 'Expand menu' : 'Collapse menu'}
                    >
                        <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
                    </button>
                )}
                {mobile && (
                    <button
                        type="button"
                        className="ecx-sidebar-toggle"
                        onClick={toggleSidebar}
                        aria-label="Close navigation menu"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                )}
            </div>

            <hr className="mx-2 my-0" />

            <nav className="ecx-sidebar-nav">
                <ul className="nav nav-pills flex-column mb-auto mt-2 gap-1">
                    {sidebarItems.map((item, idx) => {
                        if (item.type === 'divider') {
                            return (
                                <li className="nav-item" key={`divider-${idx}`}>
                                    {collapsed ? (
                                        <hr className="my-2 mx-1" />
                                    ) : (
                                        <small className={`text-uppercase fw-semibold px-2 py-1 d-block ecx-sidebar-divider ecx-sidebar-divider--${item.accent || 'default'}`}>
                                            {item.label}
                                        </small>
                                    )}
                                </li>
                            );
                        }

                        if (item.submenu) {
                            return (
                                <SubmenuItem
                                    key={item.submenuKey}
                                    item={item}
                                    collapsed={collapsed}
                                    openSubmenus={openSubmenus}
                                    toggleSubmenu={toggleSubmenu}
                                    hoveredSubmenu={hoveredSubmenu}
                                    setHoveredSubmenu={setHoveredSubmenu}
                                    submenuPosition={submenuPosition}
                                    setSubmenuPosition={setSubmenuPosition}
                                    location={location}
                                    onNavigate={mobile ? closeMobileSidebar : undefined}
                                />
                            );
                        }

                        return (
                            <NavItem
                                key={item.to}
                                to={item.to}
                                icon={item.icon}
                                label={item.label}
                                themeKey={item.theme}
                                collapsed={collapsed}
                                badge={item.badgeKey === 'smsInbox' ? smsInbox : 0}
                                onNavigate={mobile ? closeMobileSidebar : undefined}
                            />
                        );
                    })}
                </ul>
            </nav>

            <div className="ecx-sidebar-footer">
                <hr className="mx-1" />
                <ul className="nav nav-pills flex-column mb-2 gap-1">
                    <NavItem
                        to="/help"
                        icon="bi-life-preserver"
                        label="Help & Support"
                        themeKey="primary"
                        collapsed={collapsed}
                        onNavigate={mobile ? closeMobileSidebar : undefined}
                    />
                </ul>
                <button
                    type="button"
                    onClick={handleLogout}
                    className="btn btn-outline-danger ecx-logout-btn w-100 py-2 d-flex align-items-center justify-content-center"
                >
                    <span>Logout</span>
                    <i className="bi bi-box-arrow-right ms-2" />
                </button>

                {!collapsed && (
                    <div className="text-muted text-center mt-3 mb-1">
                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                            {new Date().getFullYear()} &copy; EyeconX
                        </small>
                    </div>
                )}
            </div>
        </div>
    );

    if (mobile) {
        if (!sidebarBroken) {
            return null;
        }

        return (
            <>
                {sidebarToggled && (
                    <div
                        className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 ecx-sidebar-mobile-backdrop"
                        onClick={toggleSidebar}
                        aria-hidden="true"
                    />
                )}
                <aside className={`ecx-sidebar ecx-sidebar-mobile-open ${sidebarToggled ? '' : 'd-none'}`}>
                    {sidebarContent}
                </aside>
            </>
        );
    }

    return sidebarContent;
}

export default EcxSidebar;
